/**
 * TIER 2: Shared FHIR Hooks - useFHIROptions
 *
 * Lightweight FHIR search hook specifically for populating dropdown/select options.
 * This hook is optimized for:
 * - Simple resource fetching without pagination complexity
 * - Dropdown/select option generation
 * - Minimal re-renders and dependencies
 * - Fast initial load for form fields
 *
 * Use this instead of useFHIRSearch when you just need options for form fields.
 */

// Constants
import {
  PRACTITIONER_ROLE_OPTIONS_COUNT,
  REFERRING_PROVIDER_ROLE_SEARCH_TOKEN,
} from "@benchmark/core/constants";

// Hooks
import { useMedplum } from "@medplum/react";
import {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
  startTransition,
} from "react";
import { useDebounce } from "@benchmark/ui/hooks";

// Types
import type {
  Resource,
  ResourceType,
  PractitionerRole,
  Patient,
  HealthcareService,
} from "@medplum/fhirtypes";

/** FHIR search parameters for options hooks. @agentic @benchmark/core */
export type FHIROptionsParams = {
  [key: string]: string | number | boolean | undefined;
  _count?: number;
  _sort?: string;
};

// ============================================================================
// Async Search Types
// ============================================================================

/** Select option for dropdowns. @agentic @benchmark/ui */
export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

/** Config for async FHIR search. @agentic @benchmark/core */
export type AsyncSearchConfig<T extends Resource> = {
  /** Minimum characters before search triggers (default: 2) */
  minSearchLength?: number;
  /** Debounce delay in ms (default: 300) */
  debounceMs?: number;
  /** Maximum results to return (default: 50) */
  maxResults?: number;
  /** Transform resource to label string */
  getLabel: (resource: T) => string;
  /** Optional filter predicate applied client-side */
  filter?: (resource: T) => boolean;
  /** Whether search is enabled (default: true) */
  enabled?: boolean;
  /** Custom search parameter name (default: "name:contains") */
  searchParam?: string;
};

/** Config for PractitionerRole async search hooks. @agentic @benchmark/core */
export type PractitionerRoleSearchConfig = Partial<
  Omit<AsyncSearchConfig<PractitionerRole>, "getLabel">
> & {
  /** Optional PractitionerRole.code filters (OR query with comma semantics). */
  roleCodes?: string[];
};

/** Result from async FHIR search. @agentic @benchmark/core */
export type AsyncSearchResult<T extends Resource> = {
  /** Ready-to-use options for Mantine Select */
  options: SelectOption[];
  /** True during any loading state */
  isLoading: boolean;
  /** True specifically during search (vs initial load) */
  isSearching: boolean;
  /** Error if search failed */
  error: Error | null;
  /** Clear current options (useful on blur) */
  clearOptions: () => void;
  /** Raw resources from search */
  resources: T[];
};

/** Config for useFHIROptions. @agentic @benchmark/core */
export type FHIROptionsConfig = {
  enabled?: boolean;
  onSuccess?: (data: Resource[]) => void;
  onError?: (error: Error) => void;
};

/** Result from useFHIROptions. @agentic @benchmark/core */
export type UseFHIROptionsResult<T extends Resource> = {
  data: T[];
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  isSuccess: boolean;
  refetch: () => Promise<void>;
  clearData: () => void;
};

function useFHIROptions<T extends Resource>(
  resourceType: ResourceType,
  searchParams: FHIROptionsParams = {},
  config: FHIROptionsConfig = {},
): UseFHIROptionsResult<T> {
  const { enabled = true, onSuccess, onError } = config;
  const medplum = useMedplum();

  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Store callbacks in refs to avoid dependency on their references
  // This prevents infinite loops when callers pass inline functions
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Stabilize searchParams using JSON.stringify to prevent re-renders
  // when callers pass inline objects with the same values
  // This follows the same pattern as useApiResource.ts (line 111)
  const searchParamsKey = JSON.stringify(searchParams);

  const fetchResources = useCallback(async () => {
    if (!enabled) return;

    startTransition(() => {
      setIsLoading(true);
      setError(null);
    });

    try {
      const resources = await medplum.searchResources(
        resourceType,
        searchParams,
      );
      // Ensure we always have an array, even if searchResources returns undefined/null
      const typedResources = Array.isArray(resources)
        ? (resources as unknown as T[])
        : [];

      startTransition(() => {
        setData(typedResources);
        setIsLoading(false);
      });
      onSuccessRef.current?.(typedResources);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      startTransition(() => {
        setError(error);
        setIsLoading(false);
      });
      onErrorRef.current?.(error);
    }
    // Note: searchParamsKey (JSON.stringify of searchParams) is used to stabilize the dependency
    // and prevent re-renders when callers pass inline objects with the same values.
    // onSuccess and onError are stored in refs to avoid dependency on their references.
    // This follows the same pattern as useApiResource.ts (line 111).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medplum, resourceType, enabled, searchParamsKey]);

  const refetch = useCallback(async (): Promise<void> => {
    await fetchResources();
  }, [fetchResources]);

  // Clear data manually (useful for clearing stale results on blur)
  const clearData = useCallback(() => {
    setData([]);
  }, []);

  // Execute search when dependencies change
  useEffect(() => {
    startTransition(() => {
      fetchResources();
    });
  }, [fetchResources]);

  // Derived state
  const isError = !!error;
  const isSuccess = !isLoading && !error && data.length >= 0;

  return {
    data,
    isLoading,
    error,
    isError,
    isSuccess,
    refetch,
    clearData,
  };
}

// ============================================================================
// Async Search Hook Factory
// ============================================================================

/**
 * Generic async search hook for FHIR resources.
 * Provides debounced search with automatic option transformation.
 *
 * @agentic @benchmark/core
 * @example
 * const { options, isSearching } = useFHIRAsyncSearch(
 *   "Organization",
 *   searchQuery,
 *   { type: "ins", active: "true" },
 *   { getLabel: (org) => org.name || "Unknown" }
 * );
 */
export function useFHIRAsyncSearch<T extends Resource>(
  resourceType: ResourceType,
  searchQuery: string,
  baseSearchParams: FHIROptionsParams = {},
  config: AsyncSearchConfig<T>,
): AsyncSearchResult<T> {
  const {
    minSearchLength = 2,
    debounceMs = 350,
    maxResults = 20,
    getLabel,
    filter,
    enabled = true,
    searchParam = "name:contains",
  } = config;

  const medplum = useMedplum();
  const debouncedQuery = useDebounce(searchQuery, debounceMs);

  const [resources, setResources] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Track if we're in the debounce window (user typed but search hasn't fired)
  const trimmedQuery = searchQuery.trim();
  const debouncedTrimmed = debouncedQuery.trim();
  const meetsMinLength = trimmedQuery.length >= minSearchLength;
  const debouncedMeetsMinLength = debouncedTrimmed.length >= minSearchLength;
  const isDebouncing = meetsMinLength && !debouncedMeetsMinLength;

  // Stable search params
  const searchParamsKey = JSON.stringify(baseSearchParams);

  useEffect(() => {
    if (!enabled || !debouncedMeetsMinLength) {
      queueMicrotask(() => {
        setResources([]);
      });
      return;
    }

    const controller = new AbortController();

    const performSearch = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Build search params - for chained parameters like practitioner.name,
        // we may need to handle them differently
        const searchParams: FHIROptionsParams = {
          ...baseSearchParams,
          _count: maxResults,
        };

        // Only add search param if it's not empty and searchParam is defined
        if (searchParam && debouncedTrimmed) {
          searchParams[searchParam] = debouncedTrimmed;
        }

        let results;
        try {
          results = await medplum.searchResources(resourceType, searchParams);
        } catch (searchErr) {
          // If chained search parameter fails (e.g., practitioner.name not supported),
          // fall back to fetching without name filter and filter client-side
          if (
            resourceType === "PractitionerRole" &&
            searchParam === "practitioner.name"
          ) {
            // Fallback: fetch without name search, filter client-side
            const fallbackParams: FHIROptionsParams = {
              ...baseSearchParams,
              _count: maxResults * 2, // Fetch more to account for client-side filtering
            };
            results = await medplum.searchResources(
              resourceType,
              fallbackParams,
            );
          } else {
            throw searchErr; // Re-throw if not a PractitionerRole chained search issue
          }
        }

        if (!controller.signal.aborted) {
          let typedResults = (results || []) as unknown as T[];

          // Ensure we only keep resources of the requested type (e.g., exclude _include results)
          typedResults = typedResults.filter(
            (resource) => resource.resourceType === resourceType,
          );

          // For PractitionerRole, filter by practitioner display name client-side
          // (either as fallback or to refine server-side results)
          if (
            resourceType === "PractitionerRole" &&
            searchParam === "practitioner.name" &&
            debouncedTrimmed
          ) {
            const searchLower = debouncedTrimmed.toLowerCase();
            typedResults = typedResults.filter((role) => {
              const displayName =
                (role as PractitionerRole).practitioner?.display || "";
              return displayName.toLowerCase().includes(searchLower);
            });
            // Limit results after client-side filtering
            typedResults = typedResults.slice(0, maxResults);
          }

          // Apply client-side filter if provided
          if (filter) {
            typedResults = typedResults.filter(filter);
          }

          setResources(typedResults);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    performSearch();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    medplum,
    resourceType,
    debouncedTrimmed,
    enabled,
    searchParamsKey,
    maxResults,
  ]);

  // Transform resources to options
  const options = useMemo((): SelectOption[] => {
    return resources
      .filter((r) => r.id)
      .map((r) => ({
        value: r.id as string,
        label: getLabel(r),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources, getLabel]);

  const clearOptions = useCallback(() => {
    setResources([]);
  }, []);

  return {
    options,
    isLoading,
    isSearching: isLoading || isDebouncing,
    error,
    clearOptions,
    resources,
  };
}

// ============================================================================
// Role Type Constants for Semantic Filtering
// ============================================================================

/**
 * FHIR role code token for Referring Providers (external PCPs). HL7 v2-0286|RP.
 *
 * Re-exported from `@benchmark/core` so the many local importers keep working
 * while the definition lives alongside the other PractitionerRole codes.
 * @agentic connections
 */
export const REFERRING_PROVIDER_ROLE_FILTER =
  REFERRING_PROVIDER_ROLE_SEARCH_TOKEN;

// ============================================================================
// PractitionerRole Async Search Hooks
// ============================================================================

/** Extract display label from PractitionerRole. @agentic @benchmark/core */
const getPractitionerRoleLabel = (role: PractitionerRole): string => {
  // Prefer practitioner display name
  if (role.practitioner?.display) {
    return role.practitioner.display;
  }
  // Fallback to role code display
  const codeDisplay = role.code?.[0]?.coding?.[0]?.display;
  if (codeDisplay) {
    return codeDisplay;
  }
  // Last resort: use ID
  return role.id || "Unknown";
};

/**
 * Async search for Referring Provider PractitionerRoles (external PCPs).
 * Used in Patient form for primary care provider selection.
 *
 * Filters by role code "RP" (Referring Provider) instead of static organization IDs.
 * This enables automatic discovery of all referring providers regardless of which
 * organization they belong to.
 *
 * Note: Form state should track display name separately. When user selects,
 * update both the practitioner role ID and display name in form state.
 *
 * @param searchQuery - User's search input
 * @param config - Optional configuration overrides
 * @agentic @benchmark/core
 */
export function useReferringProviderSearch(
  searchQuery: string,
  config: PractitionerRoleSearchConfig = {},
) {
  const { roleCodes, ...searchConfig } = config;
  const roleFilter = buildPractitionerRoleSearchParam(
    REFERRING_PROVIDER_ROLE_FILTER,
    roleCodes,
  );

  return useFHIRAsyncSearch<PractitionerRole>(
    "PractitionerRole",
    searchQuery,
    {
      active: "true",
      role: roleFilter,
      _sort: "practitioner",
    },
    {
      getLabel: getPractitionerRoleLabel,
      searchParam: "practitioner.name", // Search by practitioner name (chained parameter)
      ...searchConfig,
    },
  );
}

/**
 * Async search for Benchmark organization PractitionerRoles (internal staff).
 * Used in Patient form for primary advocate selection and Task form owner assignment.
 *
 * Filters by organization ID (Benchmark org) since internal staff don't have a
 * distinguishing role code - they're identified by belonging to the Benchmark organization.
 *
 * Note: Form state should track display name separately. When user selects,
 * update both the practitioner role ID and display name in form state.
 *
 * @param searchQuery - User's search input
 * @param benchmarkOrgId - Benchmark organization ID (from CONFIG.VITE_BENCHMARK_ORGANIZATION_ID)
 * @param config - Optional configuration overrides
 * @agentic @benchmark/core
 */
export function useBenchmarkStaffSearch(
  searchQuery: string,
  benchmarkOrgId: string,
  config: PractitionerRoleSearchConfig = {},
) {
  const { roleCodes, ...searchConfig } = config;
  const roleFilter = buildPractitionerRoleSearchParam(undefined, roleCodes);

  return useFHIRAsyncSearch<PractitionerRole>(
    "PractitionerRole",
    searchQuery,
    {
      active: "true",
      organization: `Organization/${benchmarkOrgId}`,
      ...(roleFilter ? { role: roleFilter } : {}),
      _sort: "practitioner",
    },
    {
      getLabel: getPractitionerRoleLabel,
      searchParam: "practitioner.name", // Search by practitioner name (chained parameter)
      maxResults: PRACTITIONER_ROLE_OPTIONS_COUNT,
      enabled: !!benchmarkOrgId,
      ...searchConfig,
    },
  );
}

/**
 * General async search for PractitionerRoles with customizable filters.
 * Used when neither referring provider nor benchmark staff filters apply.
 *
 * Note: Form state should track display name separately. When user selects,
 * update both the practitioner role ID and display name in form state.
 *
 * @param searchQuery - User's search input
 * @param searchParams - Additional FHIR search parameters (organization, role, specialty, etc.)
 * @param config - Optional configuration overrides
 * @agentic @benchmark/core
 */
export function usePractitionerRoleSearch(
  searchQuery: string,
  searchParams: FHIROptionsParams = {},
  config: PractitionerRoleSearchConfig = {},
) {
  const { roleCodes, ...searchConfig } = config;
  const roleFilter = buildPractitionerRoleSearchParam(
    typeof searchParams.role === "string" ? searchParams.role : undefined,
    roleCodes,
  );

  const stableParams = useMemo(
    () => ({
      active: "true",
      _sort: "practitioner",
      ...searchParams,
      ...(roleFilter ? { role: roleFilter } : {}),
    }),
    [roleFilter, searchParams],
  );

  return useFHIRAsyncSearch<PractitionerRole>(
    "PractitionerRole",
    searchQuery,
    stableParams,
    {
      getLabel: getPractitionerRoleLabel,
      searchParam: "practitioner.name",
      ...searchConfig,
    },
  );
}

/**
 * Builds a normalized PractitionerRole role-token query string.
 * Supports combining an existing role token filter with an array of role codes.
 * @agentic @benchmark/core
 */
export function buildPractitionerRoleSearchParam(
  existingRoleFilter?: string,
  roleCodes: string[] = [],
): string | undefined {
  const existingTokens = (existingRoleFilter ?? "")
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean);

  const roleCodeTokens = roleCodes.map((token) => token.trim()).filter(Boolean);

  const mergedTokens = [...new Set([...existingTokens, ...roleCodeTokens])];

  return mergedTokens.length > 0 ? mergedTokens.join(",") : undefined;
}

// ============================================================================
// Patient Async Search Hook
// ============================================================================

/** Extract display label from Patient. @agentic @benchmark/core */
const getPatientLabel = (patient: Patient): string => {
  const name = patient.name?.[0];
  if (name) {
    const given = name.given?.join(" ") || "";
    const family = name.family || "";
    return `${given} ${family}`.trim() || patient.id || "Unknown";
  }
  return patient.id || "Unknown";
};

/**
 * Async search for Patients.
 * Used in Task form for patient selection.
 *
 * Note: Form state should track display name separately. When user selects,
 * update both the patient ID and patient display name in form state.
 *
 * @param searchQuery - User's search input
 * @param searchParams - Additional FHIR search parameters
 * @param config - Optional configuration overrides
 * @agentic @benchmark/core
 */
export function usePatientSearch(
  searchQuery: string,
  searchParams: FHIROptionsParams = {},
  config: Partial<Omit<AsyncSearchConfig<Patient>, "getLabel">> = {},
) {
  const stableParams = useMemo(
    () => ({
      active: "true",
      _sort: "name",
      ...searchParams,
    }),
    [searchParams],
  );

  return useFHIRAsyncSearch<Patient>("Patient", searchQuery, stableParams, {
    getLabel: getPatientLabel,
    searchParam: "name", // Search by patient name
    ...config,
  });
}

// ============================================================================
// HealthcareService Async Search Hook
// ============================================================================

/** Extract display label from HealthcareService. @agentic @benchmark/core */
const getHealthcareServiceLabel = (service: HealthcareService): string => {
  return service.name || service.id || "Unknown";
};

/**
 * Async search for HealthcareServices.
 * Used in Appointment form for service selection.
 *
 * Note: Form state should track display name separately. When user selects,
 * update both the healthcare service ID and display name in form state.
 *
 * @param searchQuery - User's search input
 * @param searchParams - Additional FHIR search parameters
 * @param config - Optional configuration overrides
 * @agentic @benchmark/core
 */
export function useHealthcareServiceSearch(
  searchQuery: string,
  searchParams: FHIROptionsParams = {},
  config: Partial<Omit<AsyncSearchConfig<HealthcareService>, "getLabel">> = {},
) {
  const stableParams = useMemo(
    () => ({
      active: "true",
      _sort: "name",
      ...searchParams,
    }),
    [searchParams],
  );

  return useFHIRAsyncSearch<HealthcareService>(
    "HealthcareService",
    searchQuery,
    stableParams,
    {
      getLabel: getHealthcareServiceLabel,
      ...config,
    },
  );
}

// ============================================================================
// DEPRECATED: Static Count Hooks (replaced by async search hooks)
// ============================================================================

/**
 * @deprecated Use usePractitionerRoleSearch instead for async search capability.
 * The new hook provides debounced search with better scalability.
 * @agentic @benchmark/core
 */
export function usePractitionerRoleOptions(
  searchParams: FHIROptionsParams = {},
  config: FHIROptionsConfig = {},
) {
  const stableSearchParams = useMemo(
    () => ({
      active: "true",
      _sort: "practitioner",
      _count: PRACTITIONER_ROLE_OPTIONS_COUNT,
      ...searchParams,
    }),
    [searchParams],
  );

  return useFHIROptions("PractitionerRole", stableSearchParams, config);
}

/**
 * @deprecated Use useReferringProviderSearch instead.
 * The new hook uses role-based filtering (RP code) instead of static organization IDs.
 * This enables automatic discovery of all referring providers regardless of which
 * organization they belong to.
 * @agentic @benchmark/core
 */
export function useOrganizationOptions(
  searchParams: FHIROptionsParams = {},
  config: FHIROptionsConfig = {},
) {
  const stableSearchParams = useMemo(
    () => ({
      active: "true",
      _sort: "name",
      ...searchParams,
    }),
    [searchParams],
  );

  return useFHIROptions("Organization", stableSearchParams, config);
}

/**
 * @deprecated Use useUnifiedInsuranceSearch instead for the Patient form.
 * This hook is maintained for backward compatibility with other components.
 * @agentic @benchmark/core
 */
export function useInsuranceProviderSearch(
  searchQuery: string,
  config: FHIROptionsConfig = {},
) {
  const debouncedQuery = useDebounce(searchQuery, 300);

  const searchParams = useMemo(
    () => ({
      active: "true",
      type: "ins",
      _sort: "name",
      _count: 50,
      ...(debouncedQuery.trim() && { "name:contains": debouncedQuery.trim() }),
    }),
    [debouncedQuery],
  );

  return useFHIROptions("Organization", searchParams, {
    ...config,
    enabled: debouncedQuery.trim().length >= 2, // Only search with 2+ chars
  });
}

/**
 * Search for provider organizations (healthcare providers). Debounced.
 * @agentic @benchmark/core
 */
export function useProviderOrganizationSearch(
  searchQuery: string,
  config: FHIROptionsConfig = {},
) {
  const debouncedQuery = useDebounce(searchQuery, 300);

  const searchParams = useMemo(
    () => ({
      active: "true",
      // Only include healthcare provider types (comma = OR in FHIR search)
      type: "prov,cg,private-practice",
      _sort: "name",
      _count: 50,
      ...(debouncedQuery.trim() && { "name:contains": debouncedQuery.trim() }),
    }),
    [debouncedQuery],
  );

  return useFHIROptions("Organization", searchParams, {
    ...config,
    enabled: debouncedQuery.trim().length >= 2, // Only search with 2+ chars
  });
}
